import { Combobox, ETypeOfNotification, InputTextCustom, setNotification } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ComboDataDTO } from '../../../dto/ComboDataDTO'
import { HealthEntitySaveDTO } from '../../../dto/HealthEntitySaveDTO'
import { useHealthEntityService } from '../../../servicesHooks/HealthEntity/useHealthEntityService'

interface HealthEntityOtherDataTabProps {
  healthEntity: HealthEntitySaveDTO
  setHealthEntity: React.Dispatch<React.SetStateAction<HealthEntitySaveDTO>>
  readOnly?: boolean
}

const appointmentCategoryOptions = Array.from({ length: 26 }, (_item, idx) => String.fromCharCode(65 + idx))
  .map((value) => ({ name: value }))

type UnknownRecord = Record<string, unknown>

interface ReadOnlyFieldProps {
  label: string
  value: string | number | null | undefined
  className?: string
  emptyText?: string
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function formatReadOnlyValue(value: string | number | null | undefined, emptyText = 'Sin datos'): string {
  if (value == null) return emptyText
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return emptyText
    return String(value)
  }

  const normalized = String(value).trim()
  return normalized !== '' ? normalized : emptyText
}

function toNumericId(value: unknown): number | null {
  if (value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  if (isRecord(value)) {
    const candidates = [value.id, value.value, value.low, value.intValue, value.intCompact, value._value]
    for (const candidate of candidates) {
      const parsed = toNumericId(candidate)
      if (parsed != null) return parsed
    }
    const serialized = JSON.stringify(value)
    const match = serialized.match(/-?\d+/)
    if (match) {
      const parsed = Number(match[0])
      return Number.isFinite(parsed) ? parsed : null
    }
  }
  return null
}

function toCombo(id: unknown, name: string | null | undefined): ComboDataDTO | null {
  const normalizedId = toNumericId(id)
  const normalizedName = String(name ?? '').trim()

  if (normalizedId != null && normalizedId > 0) {
    return { id: normalizedId, name: normalizedName }
  }
  if (normalizedName !== '') {
    return { id: -1, name: normalizedName }
  }
  return null
}

function resolveLogoMimeType(logoName: string): string {
  const normalized = logoName.toLowerCase()
  if (normalized.endsWith('.jpg') || normalized.endsWith('.jpeg')) return 'image/jpeg'
  if (normalized.endsWith('.png')) return 'image/png'
  return 'application/octet-stream'
}

export default function HealthEntityOtherDataTab({
  healthEntity,
  setHealthEntity,
  readOnly = false,
}: Readonly<HealthEntityOtherDataTabProps>) {
  const dispatch = useDispatch()
  const {
    getHealthEntityRecipeLogo,
    getInformationProviders,
    getRecipeValidators,
    getStudyOrderRepositories,
    uploadHealthEntityRecipeLogo,
  } = useHealthEntityService()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)
  const [loadingLogoPreview, setLoadingLogoPreview] = useState(false)

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl)
    }
  }, [logoPreviewUrl])

  useEffect(() => {
    const logoName = String(healthEntity.recipeValidatorLogo ?? '').trim()
    if (logoName === '') {
      setLogoPreviewUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl)
        return null
      })
      return
    }

    setLoadingLogoPreview(true)
    getHealthEntityRecipeLogo(logoName)
      .then((blob) => {
        const normalizedBlob = new Blob([blob], { type: resolveLogoMimeType(logoName) })
        setLogoPreviewUrl((previousUrl) => {
          if (previousUrl) URL.revokeObjectURL(previousUrl)
          return URL.createObjectURL(normalizedBlob)
        })
      })
      .catch(() => {
        setLogoPreviewUrl((previousUrl) => {
          if (previousUrl) URL.revokeObjectURL(previousUrl)
          return null
        })
      })
      .finally(() => setLoadingLogoPreview(false))
  }, [healthEntity.recipeValidatorLogo])

  function handleLogoUploadChange(event: ChangeEvent<HTMLInputElement>) {
    if (readOnly) {
      event.target.value = ''
      return
    }

    const file = event.target.files?.[0]
    if (!file) return

    const fileNameLower = file.name.toLowerCase()
    const isValidFile = fileNameLower.endsWith('.png') || fileNameLower.endsWith('.jpg') || fileNameLower.endsWith('.jpeg')
    if (!isValidFile) {
      dispatch(setNotification({
        title: 'Archivo inválido',
        desc: 'Solo se permiten imágenes JPG o PNG',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      event.target.value = ''
      return
    }

    setUploadingLogo(true)
    uploadHealthEntityRecipeLogo(file, healthEntity.recipeValidatorLogo ?? '')
      .then((uploadedName) => {
        setHealthEntity(prev => ({
          ...prev,
          recipeValidatorLogo: uploadedName ?? '',
        }))
        dispatch(setNotification({
          title: 'Logotipo cargado',
          desc: 'La imagen se guardará al confirmar la obra social',
          typeOfNotification: ETypeOfNotification.INFO,
          icon: 'pi pi-check',
        }))
      })
      .catch(() => {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo cargar el logotipo',
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
      })
      .finally(() => {
        setUploadingLogo(false)
        event.target.value = ''
      })
  }

  function removeUploadedLogo() {
    if (readOnly) return

    const currentLogoName = String(healthEntity.recipeValidatorLogo ?? '').trim()
    if (currentLogoName === '') return

    setLogoPreviewUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl)
      return null
    })
    setHealthEntity(prev => ({
      ...prev,
      recipeValidatorLogo: '',
    }))

    if (logoInputRef.current) {
      logoInputRef.current.value = ''
    }

    dispatch(setNotification({
      title: 'Logotipo eliminado',
      desc: 'Se quitará la imagen al guardar la obra social',
      typeOfNotification: ETypeOfNotification.INFO,
      icon: 'pi pi-check',
    }))
  }

  function ReadOnlyField({ label, value, className = '', emptyText = 'Sin datos' }: Readonly<ReadOnlyFieldProps>) {
    const normalizedValue = formatReadOnlyValue(value, emptyText)
    const isEmptyValue = normalizedValue === emptyText

    return (
      <div className={`health-entity-readonly-field ${className}`.trim()}>
        <span className='health-entity-readonly-field-label'>{label}</span>
        {isEmptyValue ? (
          <em
            className='health-entity-readonly-field-value health-entity-readonly-field-value-muted'
            style={{ fontFamily: 'sans-serif', fontStyle: 'italic' }}
          >
            {normalizedValue}
          </em>
        ) : (
          <span className='health-entity-readonly-field-value'>{normalizedValue}</span>
        )}
      </div>
    )
  }

  return (
    <div className='health-entity-other-data-tab'>
      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Recetas</h4>
        {readOnly ? (
          <div className='health-entity-other-data-grid'>
            <ReadOnlyField label='Validador de recetas' value={healthEntity.recipeValidatordesc} />
            <ReadOnlyField label='ID' value={healthEntity.idForRecipeValidator} />
            <ReadOnlyField label='Vademécum de medicamentos' value={healthEntity.vademecumdesc} />
          </div>
        ) : (
          <div className='health-entity-other-data-grid'>
            <div className='health-entity-general-field'>
              <label className='block mb-2'>Validador de recetas</label>
              <Combobox
                className='w-full health-entity-dark-combobox'
                optionLabel='name'
                value={toCombo(healthEntity.recipeValidator, healthEntity.recipeValidatordesc)}
                getItems={getRecipeValidators}
                placeholder='Seleccionar'
                disable={readOnly}
                setValue={(selected: ComboDataDTO | null) => {
                  setHealthEntity(prev => ({
                    ...prev,
                    recipeValidator: toNumericId(selected?.id),
                    recipeValidatordesc: selected?.name ?? '',
                  }))
                }}
              />
            </div>

            <div className='health-entity-general-field'>
              <label className='block mb-2'>ID</label>
              <InputTextCustom
                className='w-full health-entity-id-input'
                value={healthEntity.idForRecipeValidator}
                disable={readOnly}
                onChange={(e) => setHealthEntity(prev => ({ ...prev, idForRecipeValidator: e.target.value }))}
              />
            </div>

            <div className='health-entity-general-field'>
              <label className='block mb-2'>Vademécum de medicamentos</label>
              <Combobox
                className='w-full health-entity-dark-combobox'
                optionLabel='name'
                value={toCombo(healthEntity.vademecum, healthEntity.vademecumdesc)}
                getItems={getInformationProviders}
                placeholder='Seleccionar'
                disable={readOnly}
                setValue={(selected: ComboDataDTO | null) => {
                  setHealthEntity(prev => ({
                    ...prev,
                    vademecum: toNumericId(selected?.id),
                    vademecumdesc: selected?.name ?? '',
                  }))
                }}
              />
            </div>
          </div>
        )}

        <div className='health-entity-logo-upload-block'>
          <p className='health-entity-logo-upload-label'>Logotipo para recetas</p>
          {!readOnly && !healthEntity.recipeValidatorLogo && (
            <>
              <input
                ref={logoInputRef}
                type='file'
                accept='.png,.jpg,.jpeg'
                className='hidden'
                onChange={handleLogoUploadChange}
              />
              <Button
                className='health-entity-logo-upload-btn'
                label={uploadingLogo ? 'Cargando imagen...' : 'Cargar imagen (JPG, PNG)'}
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              />
            </>
          )}

          {healthEntity.recipeValidatorLogo ? (
            <div className='health-entity-logo-upload-preview-row'>
              <small className='health-entity-logo-upload-caption'>
                Archivo seleccionado: {healthEntity.recipeValidatorLogo}
              </small>
              {loadingLogoPreview && <small className='health-entity-logo-upload-caption'>Cargando vista previa...</small>}
              {logoPreviewUrl && (
                <img
                  className='health-entity-logo-preview-inline'
                  src={logoPreviewUrl}
                  alt='Logo cargado de la obra social'
                />
              )}
              {!readOnly && (
                <Button
                  className='health-entity-logo-remove-btn'
                  icon='pi pi-trash'
                  text
                  rounded
                  onClick={removeUploadedLogo}
                  aria-label='Eliminar imagen'
                />
              )}
            </div>
          ) : (
            readOnly && (
              <em
                className='health-entity-readonly-field-value health-entity-readonly-field-value-muted'
                style={{ fontFamily: 'sans-serif', fontStyle: 'italic' }}
              >
                Sin logo cargado
              </em>
            )
          )}
        </div>
      </section>

      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Órdenes de estudios</h4>
        <div className='health-entity-other-data-row'>
          <div className='health-entity-general-field w-full md:w-6'>
            {readOnly ? (
              <ReadOnlyField label='Repositorio de órdenes de estudio' value={healthEntity.studyOrderRepositorydesc} />
            ) : (
              <>
                <label className='block mb-2'>Repositorio de órdenes de estudio</label>
                <Combobox
                  className='w-full health-entity-dark-combobox'
                  optionLabel='name'
                  value={toCombo(healthEntity.studyOrderRepository, healthEntity.studyOrderRepositorydesc)}
                  getItems={getStudyOrderRepositories}
                  placeholder='Seleccionar'
                  disable={readOnly}
                  setValue={(selected: ComboDataDTO | null) => {
                    setHealthEntity(prev => ({
                      ...prev,
                      studyOrderRepository: toNumericId(selected?.id),
                      studyOrderRepositorydesc: selected?.name ?? '',
                    }))
                  }}
                />
              </>
            )}
          </div>
        </div>
      </section>

      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Turnos</h4>
        <div className='health-entity-other-data-row'>
          <div className='health-entity-general-field w-full md:w-6'>
            {readOnly ? (
              <ReadOnlyField label='Categoría de turnos' value={healthEntity.appointmentsCategory} />
            ) : (
              <>
                <label className='block mb-2'>Categoría de turnos</label>
                <Combobox
                  className='w-full health-entity-dark-combobox'
                  list={appointmentCategoryOptions}
                  optionLabel='name'
                  value={healthEntity.appointmentsCategory ? { name: healthEntity.appointmentsCategory } : null}
                  placeholder='Seleccionar'
                  disable={readOnly}
                  setValue={(value: { name?: string } | null) => {
                    setHealthEntity(prev => ({
                      ...prev,
                      appointmentsCategory: value?.name ?? null,
                    }))
                  }}
                />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

